/**
 * Visualizador de DANFE (NF-e modelo 55) em PDF.
 */

import { useState, useEffect, useCallback } from 'react'
import { FileText, AlertCircle, Loader2 } from 'lucide-react'
import ModalERP from '../../ModalERP'
import api from '../../../../services/api'
import './DanfeViewerModal.css'

function DanfeViewerModal({ isOpen, onClose, url, title = 'DANFE — NF-e' }) {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [blobUrl, setBlobUrl] = useState(null)

  const parseBlobErrorMessage = async (err) => {
    const data = err.response?.data
    if (data instanceof Blob) {
      try {
        const text = await data.text()
        const json = JSON.parse(text)
        if (json?.message) return json.message
      } catch {
        // resposta não é JSON
      }
    }
    if (data?.message) return data.message
    if (err.response?.status === 401) {
      return 'Erro de autenticação. Faça login novamente.'
    }
    return 'Erro ao carregar o DANFE.'
  }

  const loadPdfAsBlob = useCallback(async (pdfUrl) => {
    setLoading(true)
    setError(null)
    setBlobUrl(null)

    try {
      const response = await api.get(pdfUrl, {
        responseType: 'blob',
        headers: { Accept: 'application/pdf, */*' }
      })

      const contentType = (response.headers?.['content-type'] || '').toLowerCase()
      const blobType = (response.data?.type || '').toLowerCase()
      if (contentType.includes('json') || blobType.includes('json')) {
        const text = await response.data.text()
        const json = JSON.parse(text)
        throw new Error(json.message || 'DANFE não disponível')
      }

      const mimeType = contentType.includes('pdf')
        ? 'application/pdf'
        : (response.data?.type || 'application/pdf')

      const blob = new Blob([response.data], { type: mimeType })
      const objectUrl = URL.createObjectURL(blob)
      setBlobUrl(objectUrl)
      setLoading(false)
    } catch (err) {
      const message =
        err.message && !err.response ? err.message : await parseBlobErrorMessage(err)
      setError(message)
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    if (!isOpen || !url) return

    const needsBlob = url.includes('/api/') || url.includes('localhost:3000')
    if (needsBlob) {
      loadPdfAsBlob(url)
    } else {
      setLoading(false)
      setError(null)
    }
  }, [isOpen, url, loadPdfAsBlob])

  useEffect(() => {
    if (!isOpen && blobUrl) {
      URL.revokeObjectURL(blobUrl)
      setBlobUrl(null)
    }
  }, [isOpen, blobUrl])

  const handleClose = () => {
    if (blobUrl) {
      URL.revokeObjectURL(blobUrl)
      setBlobUrl(null)
    }
    setLoading(true)
    setError(null)
    onClose()
  }

  const iframeSrc = blobUrl || (url && !url.includes('/api/') && !url.includes('localhost:3000') ? url : null)

  return (
    <ModalERP
      isOpen={isOpen}
      onClose={handleClose}
      title={title}
      subtitle="Documento Auxiliar da Nota Fiscal Eletrônica"
      icon={<FileText size={22} />}
      size="full"
      footer={
        <button type="button" className="modal-erp-btn-secondary" onClick={handleClose}>
          Fechar
        </button>
      }
    >
      <div className="danfe-viewer-body">
        {loading && (
          <div className="danfe-viewer-state">
            <Loader2 size={32} className="modal-erp-spinner" />
            <p>Carregando DANFE...</p>
          </div>
        )}
        {error && (
          <div className="danfe-viewer-state danfe-viewer-error">
            <AlertCircle size={28} />
            <p>{error}</p>
            {url && (
              <button
                type="button"
                className="modal-erp-btn-secondary"
                onClick={() => loadPdfAsBlob(url)}
              >
                Tentar novamente
              </button>
            )}
          </div>
        )}
        {iframeSrc && !error && (
          <iframe
            src={iframeSrc}
            className="danfe-viewer-iframe"
            title={title}
            onLoad={() => setLoading(false)}
            style={{ display: loading ? 'none' : 'block' }}
          />
        )}
      </div>
    </ModalERP>
  )
}

export default DanfeViewerModal
